// Twitch chat menu component for the library

import React from 'react';
import { POPPY_CHAT_COMMAND_NAMESPACE } from '@airhead-games/shared/configs';
import {
  ChevronRightIcon,
  CommandIcon,
  LinkIcon,
  MessageCircle,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
} from '../../../components/ui/popover';
import { Separator } from '../../../components/ui/separator';
import { Switch } from '../../../components/ui/switch';
import type { PoppyChatViewModel } from '../hooks';

type PoppyChatMenuProps = {
  viewModel: PoppyChatViewModel;
};

const PoppyChatMenu: React.FC<PoppyChatMenuProps> = ({ viewModel }) => {
  return (
    <>
      <PopoverHeader>
        <PopoverTitle>Poppy in Twitch Chat</PopoverTitle>
        <PopoverDescription>
          Let Poppy help moderate and engage your chat while playing Airhead
          Games.
        </PopoverDescription>
      </PopoverHeader>
      <Separator />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Auto-post room join link</div>
            <p className="text-xs text-muted-foreground">
              Automatically post the join link in your chat when you open a new
              game room.
            </p>
          </div>
          <Switch
            aria-label="Auto-post room join link"
            checked={viewModel.features.autoPostJoinLink}
            onCheckedChange={(checked) =>
              viewModel.onFeatureToggle('autoPostJoinLink', checked)
            }
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Respond to commands</div>
            <p className="text-xs text-muted-foreground">
              Poppy responds with information when you use{' '}
              {POPPY_CHAT_COMMAND_NAMESPACE} commands.
            </p>
          </div>
          <Switch
            aria-label="Respond to commands"
            checked={viewModel.features.chatCommands}
            onCheckedChange={(checked) =>
              viewModel.onFeatureToggle('chatCommands', checked)
            }
          />
        </div>
        <Button
          variant="ghost"
          className="h-auto w-full justify-start gap-2 px-2 py-2 text-left"
          onClick={viewModel.onViewCommandsClick}
        >
          <CommandIcon data-icon="inline-start" />
          <span>View chat commands</span>
          <ChevronRightIcon
            data-icon="inline-end"
            className="ml-auto text-muted-foreground"
          />
        </Button>
        {viewModel.showConnectTwitchRow && (
          <Button
            variant="ghost"
            className="h-auto w-full justify-start gap-2 px-2 py-2 text-left"
            onClick={viewModel.onConnectTwitchRowClick}
          >
            <LinkIcon data-icon="inline-start" />
            <span>Connect Twitch</span>
            <ChevronRightIcon
              data-icon="inline-end"
              className="ml-auto text-muted-foreground"
            />
          </Button>
        )}
      </div>
    </>
  );
};

export const POPPY_CHAT_MENU_ICON = <MessageCircle />;
export default PoppyChatMenu;
